<template>
  <b-sidebar
    id="sidebar-flugplanung"
    backdrop-variant="dark"
    backdrop
    shadow
    right
    width="max(700px,50%)"
    @shown="onShow"
    @hidden="onHide"
  >
    <template #header="{ hide }">
      <SidebarFlugplanungHeader
        :isLoading="isLoading"
        :reisetermin="reisetermin"
        :disableSpeichernButton="disableSpeichernButton"
        @go-up="goUpOneRow"
        @go-down="goDownOneRow"
        @aufgabe-erstellen="openModalWithData"
        @save="saveChanges"
        @close="hide"
      />
    </template>
    <b-overlay :show="isLoading" opacity="0.95" style="min-height: 250px">
      <div
        class="px-6 py-4 d-flex"
        v-if="reiseterminExists && Object.keys(this.reisetermin).length > 0"
        style="width: 100%; flex-direction: column"
      >
        <div class="d-flex mb-4" style="gap: 8px">
          <b-button
            v-for="tab in tabs"
            :key="tab.text"
            size="sm"
            :class="activeTab === tab.text ? 'dialog-btn' : 'dialog-btn-secondary'"
            :variant="activeTab === tab.text ? 'primary' : 'secondary'"
            @click="activeTab = tab.text"
          >
            <div v-if="tab.show">
              {{ tab.text }}
            </div>
          </b-button>
        </div>
        <div v-if="activeTab === 'Allgemein'">
          <CollapsibleWithItemsEditable
            :header="'Reise & Flugdaten'"
            v-model="dataReiseUndFlugdaten"
            :isCollapsedOnMount="true"
          ></CollapsibleWithItemsEditable>
          <CollapsibleWithItemsEditable
            :country="reisetermin ? reisetermin.reise.reisekuerzel.substring(0, 2) : ''"
            :header="'Einkaufsdaten'"
            v-model="dataEinkaufsdaten"
            :isCollapsedOnMount="true"
            class="mt-4"
          ></CollapsibleWithItemsEditable>

          <SidebarFlugplanungGruppeneinkaeufe
            ref="gruppeneinkaeufe"
            :reisetermin="reisetermin"
            @changed="onGruppeneinkaufChanged"
            @leitflugnummer-changed="onLeitflugnummerChanged"
            @loading="isLoading = $event"
            @createdGruppeneinkauf="$emit('createdGruppeneinkauf', $event)"
            @newGruppeneinkauf="$emit('newGruppeneinkauf', $event)"
            @updatedGruppeneinkauf="$emit('updatedGruppeneinkauf', $event)"
            @deletedGrpFlughafen="$emit('deletedGrpFlughafen', $event)"
          />

          <SidebarFlugplanungInlandsfluege
            :dataInlandsfluege="dataInlandsfluege"
            @flugnummer-changed="changedFlugnummerOrInGruppeInkludiert"
          />

          <!-- Aufgaben -->
          <div v-if="aufgabenTerminplanung?.length > 0">
            <h5
              role="button"
              @click="isAufgabenCollapsed = !isAufgabenCollapsed"
              :aria-expanded="isAufgabenCollapsed ? 'false' : 'true'"
              aria-controls="collapse-5"
              class="mb-2 user-select-none mt-4"
            >
              <b-icon
                v-if="!isAufgabenCollapsed"
                :scale="0.75"
                icon="chevron-right"
                aria-hidden="true"
              ></b-icon>
              <b-icon v-else :scale="0.75" icon="chevron-down" aria-hidden="true"></b-icon>
              Aufgaben
            </h5>
            <hr class="w-100 ml-0" />
            <b-collapse
              id="collapse-5"
              :class="isAufgabenCollapsed ? null : 'collapsed'"
              v-model="isAufgabenCollapsed"
            >
              <AufgabenWidget
                class="mt-3"
                :filter="'sidebar-flugplanung'"
                :ansicht="'sidebar-flugplanung'"
                :aufgaben="aufgabenTerminplanung || []"
              ></AufgabenWidget>
            </b-collapse>
          </div>

          <!-- Kommentare -->
          <div class="mt-6">
            <ReiseterminComments
              :reiseterminId="reisetermin.id"
              :comments="getCommentsWithMappedAnswers"
              @updateComments="onUpdateComments"
              :hideEmojis="true"
            />
          </div>
        </div>
        <div v-if="activeTab === 'Verlauf'">
          <DataChangeHistory :data="verlaufData"></DataChangeHistory>
        </div>
      </div>
    </b-overlay>
  </b-sidebar>
</template>
<script>
import AufgabenWidget from '@/components/produkte/reisetermine/aufgaben-widget.vue';
import CollapsibleWithItemsEditable from '@/components/common/collapsible-with-items-editable.vue';
import apiService from '@/core/common/services/api.service';
import * as odataService from '@/core/common/services/odata.service';
import ReiseterminComments from '@/components/comments/reisetermin-comments.vue';
import DataChangeHistory from '@/components/produkte/reisen/data-change-history.vue';
import SidebarFlugplanungHeader from '@/components/produkte/reisetermine/sidebar-flugplanung/sidebar-flugplanung-header.vue';
import SidebarFlugplanungGruppeneinkaeufe from '@/components/produkte/reisetermine/sidebar-flugplanung/sidebar-flugplanung-gruppeneinkaeufe.vue';
import SidebarFlugplanungInlandsfluege from '@/components/produkte/reisetermine/sidebar-flugplanung/sidebar-flugplanung-inlandsfluege.vue';
import {
  buildEinkaufsdaten,
  buildReiseUndFlugdaten,
  buildInlandsfluegeViewModel,
} from './helpers/feldkonfiguration';
import { buildStatusgruppeOptions, buildEinkaufsartOptions } from './helpers/einkaufsart-statusgruppe';
import { areValuesEqual } from '@/core/common/helpers/utils';

export default {
  props: { reiseterminId: { type: Number } },
  components: {
    CollapsibleWithItemsEditable,
    ReiseterminComments,
    AufgabenWidget,
    DataChangeHistory,
    SidebarFlugplanungHeader,
    SidebarFlugplanungGruppeneinkaeufe,
    SidebarFlugplanungInlandsfluege,
  },
  data() {
    return {
      isAufgabenCollapsed: false,
      prefixedVerlinkungen: [],
      dataEinkaufsdaten: [],
      dataReiseUndFlugdaten: [],
      isLoading: false,
      resultsAirlines: [],
      reisetermin: null,
      activeTab: 'Allgemein',
      verlaufData: {},
      // Änderungsstand der Gruppeneinkauf-Sektion
      gruppeneinkaufHasChanges: false,
      expand: {
        reise: {},
        metadata: {
          expand: {
            inlandsfluege: {},
            ziellandAbflughaefen: {},
            ziellandZielflughaefen: {},
          },
        },
        pax: {},
        gueltigerSaisonpreis: {},
        flugeinkauf: { expand: { Gruppeneinkaeufe: {} } },
        aufgaben: {},
        comments: {},
        ursprungsreisetermin: {},
        ersatztermin: {},
      },
    };
  },
  watch: {
    // Wechselt der angezeigte Reisetermin, wird er neu geladen und die Datenblöcke werden neu aufgebaut
    reiseterminId: {
      immediate: true,
      async handler(id) {
        if (id != null) {
          this.verlaufData = {};
          await this.loadReisetermin(id);
          this.setEinkaufsdaten();
          this.setReiseUndFlugdaten();

          // Der Verlauf wird nur bei aktivem Verlauf-Tab geladen, um unnötige Requests zu vermeiden
          if (this.activeTab === 'Verlauf') {
            const { id: reiseterminId, reiseId, startdatum } = this.reisetermin;
            this.loadVerlauf(reiseterminId, reiseId, startdatum);
          }
        }
      },
    },
    // Beim Wechsel auf den Verlauf-Tab werden die Verlaufsdaten nachgeladen
    activeTab() {
      if (this.activeTab === 'Verlauf') {
        const { id: reiseterminId, reiseId, startdatum } = this.reisetermin;
        this.loadVerlauf(reiseterminId, reiseId, startdatum);
      }
    },
    // Hält die auswählbaren Optionen und den Hinweis von Status Gruppe / Einkaufsart konsistent
    dataEinkaufsdaten: {
      deep: true,
      handler() {
        const einkaufsartItem = this.dataEinkaufsdaten[1];
        const statusgruppeItem = this.dataEinkaufsdaten[0];
        if (!einkaufsartItem || !statusgruppeItem) return;

        const einkaufsart = einkaufsartItem.valueEdited;
        // Bei Wechsel der Einkaufsart auf 'Ep' wird die Status Gruppe einmalig auf 'Ep' vorbelegt
        if (einkaufsart !== this._lastEinkaufsart) {
          if (einkaufsart === 'Ep') {
            statusgruppeItem.valueEdited = 'Ep';
          }
          this._lastEinkaufsart = einkaufsart;
          // Status-Gruppe-Optionen an die neue Einkaufsart anpassen
          statusgruppeItem.options = buildStatusgruppeOptions(einkaufsart, statusgruppeItem.valueEdited);
        }

        const statusgruppe = statusgruppeItem.valueEdited;
        if (statusgruppe !== this._lastStatusgruppe) {
          this._lastStatusgruppe = statusgruppe;
          einkaufsartItem.options = buildEinkaufsartOptions(statusgruppe, einkaufsartItem.valueEdited);
        }

        // Hinweis wenn eine Einkaufsart gewählt ist, aber die Status Gruppe fehlt
        statusgruppeItem.hint =
          einkaufsart != null && statusgruppe == null
            ? 'Status Gruppe fehlt – bitte gemeinsam mit Einkaufsart ausfüllen.'
            : null;
      },
    },
  },
  computed: {
    // Ordnet die Antwort-Kommentare ihren übergeordneten Kommentaren zu und sortiert nach Datum (neueste zuerst)
    // Kommentare mit parentId sind Antworten; Kommentare ohne parentId sind die Wurzel-Kommentare
    getCommentsWithMappedAnswers() {
      if (!this.reisetermin || !this.reisetermin.comments?.length > 0) {
        return [];
      }

      const answers = this.reisetermin.comments.filter(comment => comment.parentId !== null);
      const commentsWithAnswers = this.reisetermin.comments
        .filter(comment => comment.parentId === null)
        .map(comment => {
          const matchingAnswers = answers.filter(answer => answer.parentId === comment.id);
          if (matchingAnswers.length > 0) {
            return { ...comment, answers: matchingAnswers };
          } else return { ...comment, answers: [] };
        })
        .sort((a, b) => new Date(b.createdDateUtc) - new Date(a.createdDateUtc));
      return commentsWithAnswers;
    },
    // Liefert die Aufgaben des Reisetermins oder null, wenn keine vorhanden sind (steuert die Sichtbarkeit der Sektion)
    aufgabenTerminplanung() {
      if (!this.reisetermin || this.reisetermin.aufgaben?.length === 0) {
        return null;
      }

      return this.reisetermin.aufgaben;
    },
    // True, sobald ein Reisetermin mit Daten geladen wurde; verhindert das Rendern vor dem Laden
    reiseterminExists() {
      if (this.reisetermin) return Object.keys(this.reisetermin)?.length > 0;

      return false;
    },
    dataInlandsfluege() {
      if (!this.reiseterminExists) return [];
      return buildInlandsfluegeViewModel(this.reisetermin);
    },
    // True, wenn eine Einkaufsart gewählt wurde, aber die dazu erforderliche Status Gruppe fehlt (ungültiger Zustand)
    einkaufsartOhneStatusgruppe() {
      const statusgruppe = this.dataEinkaufsdaten[0]?.valueEdited;
      const einkaufsart = this.dataEinkaufsdaten[1]?.valueEdited;
      return einkaufsart != null && statusgruppe == null;
    },
    // Der Speichern-Button ist nur aktiv, wenn der Zustand gültig ist und mindestens ein Bereich Änderungen hat
    disableSpeichernButton() {
      if (this.einkaufsartOhneStatusgruppe) return true;

      return (
        this.noDataEinkausdatenChanges &&
        this.noDataReiseUndFlugdatenChanges &&
        !this.gruppeneinkaufHasChanges
      );
    },
    // True, wenn kein bearbeitbares Einkaufsdaten-Feld gegenüber seinem Ausgangswert verändert wurde
    noDataEinkausdatenChanges() {
      return this.dataEinkaufsdaten
        .filter(item => item.editable)
        .every(item => areValuesEqual(item.value, item.valueEdited));
    },
    // True, wenn kein bearbeitbares Reise- und Flugdaten-Feld gegenüber seinem Ausgangswert verändert wurde
    noDataReiseUndFlugdatenChanges() {
      return this.dataReiseUndFlugdaten
        .filter(item => item.editable)
        .every(item => areValuesEqual(item.value, item.valueEdited));
    },
    tabs() {
      return [
        { show: true, text: 'Allgemein' },
        { show: true, text: 'Verlauf' },
      ];
    },
  },
  mounted() {
    if (this.reiseterminExists) {
      this.setEinkaufsdaten();
    }
  },
  methods: {
    onUpdateComments(res) {
      this.reisetermin.comments = res.comments;
    },
    // Lädt den Reisetermin samt der über expand definierten Beziehungen per OData-Filter auf die Id
    async loadReisetermin(id) {
      this.isLoading = true;
      const response = await odataService.getReisetermin({
        filter: [{ id: { eq: id } }],
        expand: this.expand,
      });
      this.isLoading = false;
      this.reisetermin = response.data[0];
    },
    // Speichert Flugnummer und "In Gruppe inkludiert" eines Inlandsflugs sofort per API und meldet die Änderung nach außen
    changedFlugnummerOrInGruppeInkludiert(inlandsflugData) {
      this.isLoading = true;

      const flugnummer = inlandsflugData.find(item => item.label === 'Flugnummer').valueEdited;
      const inGruppeInkludiert = inlandsflugData.find(
        item => item.label === 'In Gruppe inkludiert'
      ).valueEdited;
      const inlandsflugId = inlandsflugData.find(item => item.label === 'Id')?.value;

      const { id: reiseterminId } = this.reisetermin;

      apiService
        .put('Reisetermin/metadata/inlandsflug/bulkUpdateFlugnummerAndInGruppeInkludiert', {
          reiseterminIdToInlandsflugId: {
            [reiseterminId]: inlandsflugId,
          },
          flugnummer,
          inGruppeInkludiert,
        })
        .then(() => {
          this.toast('Flugnummer gespeichert!');
          this.$emit('inlandsflugFlugnummerChanged', {
            flugnummer,
            inGruppeInkludiert,
            reiseterminId,
            inlandsflugId,
          });
        })
        .finally(() => (this.isLoading = false));
    },
    // Baut das Payload-Objekt für den Flugeinkauf-Endpunkt, gruppiert nach dem Startdatum des Reisetermins
    // Der Zugriff erfolgt über feste Array-Indizes, daher darf die Feldreihenfolge in buildEinkaufsdaten nicht geändert werden
    formatParams() {
      const params = {
        [this.reisetermin.startdatum]: {
          statusgruppe: this.dataEinkaufsdaten[0].valueEdited ?? null,
          einkaufsart: this.dataEinkaufsdaten[1].valueEdited ?? null,
          mindestabnahmeAirline: this.dataEinkaufsdaten[2].valueEdited ?? null,
          optionsfrist: this.dataEinkaufsdaten[3].valueEdited[0] ?? null,
          ticketfrist: this.dataEinkaufsdaten[4].valueEdited[0] ?? null,
        },
      };
      return params;
    },
    // Baut das Payload-Objekt für die TH-Werte (Langstrecke und Inlandsflug), ebenfalls nach Startdatum gruppiert
    // Index 9 ist das "TH Langstrecke / TH Inlandsflug"-Feld aus buildReiseUndFlugdaten; das + wandelt in eine Zahl um
    formatReiseTerminParams() {
      const ReiseterminParams = {
        [this.reisetermin.startdatum]: {
          thLangstrecke: +this.dataReiseUndFlugdaten[9].valueEdited[0] || null,
          thInlandsflug: +this.dataReiseUndFlugdaten[9].valueEdited[1] || null,
        },
      };
      return ReiseterminParams;
    },
    // Speichert alle geänderten Bereiche nacheinander (Flugeinkauf, TH-Werte, Gruppeneinkäufe) und lädt danach neu
    async saveChanges() {
      this.isLoading = true;

      const params = this.formatParams();
      const reiseTerminParams = this.formatReiseTerminParams();

      const reisekuerzel = this.reisetermin.reiseterminkuerzel.substring(0, 6);

      // in dem array "dataReiseUndFlugdaten" wird das Objekt mit dem label "TH Langstrecke" geholt
      const objectThWerte = this.dataReiseUndFlugdaten.find(element => element.label[0] === 'TH Langstrecke');
      // hier wird dann in dem geholten Objekt überprüft, ob sich die werte geändert haben
      const isChangedThWerte =
        objectThWerte.value[0] != objectThWerte.valueEdited[0] ||
        objectThWerte.value[1] != objectThWerte.valueEdited[1];

      try {
        // API-Aufruf für Flugeinkauf-Daten
        if (!this.noDataEinkausdatenChanges) {
          await apiService
            .put(`Reise/${reisekuerzel}/reisetermine/metadata/flugeinkauf`, params)
            .then(response => {
              this.$emit('singleFlugplanungBearbeitet', response);
              this.toast('Flugeinkauf erfolgreich gespeichert.');
            });
        }

        // wenn ThWerte verändert wurden
        if (isChangedThWerte) {
          // API-Aufruf für thLangstrecke und thInlandsflug
          await apiService
            .put(`Reise/${reisekuerzel}/reisetermine/bulkUpdateReiseterminValues`, reiseTerminParams)
            .then(response => {
              this.$emit('thWerteBearbeitet', response);
              this.toast('TH-Werte erfolgreich gespeichert.');
            });
        }

        // Gruppeneinkäufe speichern (Erstellen und Aktualisieren übernimmt die Sub-Komponente)
        if (this.$refs.gruppeneinkaeufe) {
          await this.$refs.gruppeneinkaeufe.save();
        }

        // Reisetermin neu laden um aktualisierte Daten anzuzeigen
        await this.loadReisetermin(this.reiseterminId);
        this.setEinkaufsdaten();
        this.setReiseUndFlugdaten();
      } catch (error) {
        this.isLoading = false;
      } finally {
        this.isLoading = false;
      }
    },
    // Baut die Einkaufsdaten-Felder neu auf
    // Die _last-Felder merken sich den Ausgangsstand, damit der dataEinkaufsdaten-Watcher echte Wechsel erkennt
    setEinkaufsdaten() {
      this._lastEinkaufsart = this.reisetermin.flugeinkauf?.einkaufsart;
      this._lastStatusgruppe = this.reisetermin.flugeinkauf?.statusgruppe;
      this.dataEinkaufsdaten = buildEinkaufsdaten(this.reisetermin);
    },
    // Baut die Reise- und Flugdaten-Felder neu auf
    setReiseUndFlugdaten() {
      this.dataReiseUndFlugdaten = buildReiseUndFlugdaten(this.reisetermin);
    },
    // Führt den Airline-Bezug aus, wenn die führende Flugnummer im Gruppeneinkauf geändert wurde
    onLeitflugnummerChanged({ newValue, oldValue }) {
      const airlineKuerzel = newValue?.substring(0, 2);
      const oldAirlineKuerzel = oldValue?.substring(0, 2);
      if (!this.resultsAirlines.some(result => result.airlineKuerzel === airlineKuerzel)) {
        if (newValue != null && newValue?.length >= 2 && airlineKuerzel !== oldAirlineKuerzel) {
          apiService.get(`airline?$filter=airlineKuerzel eq '${airlineKuerzel}'`).then(res => {
            const { result } = res.data;
            if (result.length === 1) {
              this.resultsAirlines.push(result[0]);
              this.$set(this.dataEinkaufsdaten[3].valueEdited, 0, result[0].optionsfrist);
              this.$set(this.dataEinkaufsdaten[4].valueEdited, 0, result[0].ticketingfrist);
            } else {
              this.resultsAirlines.push({ airlineKuerzel });
            }
          });
        }
      }
    },
    // Übernimmt den Änderungsstand der Gruppeneinkauf-Sektion für den zentralen Speichern-Button
    onGruppeneinkaufChanged({ hasChanges }) {
      this.gruppeneinkaufHasChanges = hasChanges;
    },
    // Beim Öffnen der Sidebar wird der Standard-Tab gesetzt und die Tastatur-Navigation aktiviert
    onShow() {
      this.activeTab = 'Allgemein';
      this.addKeyEventListener();
    },
    // Beim Schließen wird der Airline-Cache geleert, das Schließen gemeldet und der Tastatur-Listener entfernt
    onHide() {
      this.resultsAirlines = [];

      this.$emit('sidebarHide');
      this.removeKeyEventListener();
    },
    // Navigiert zur vorherigen Grid-Zeile; der Airline-Cache wird geleert, damit der Airline-Bezug neu greift
    goUpOneRow() {
      if (!this.isLoading) {
        this.resultsAirlines = [];
        this.$emit('rowSelectionChanged', 'up');
      }
    },
    // Navigiert zur nächsten Grid-Zeile; der Airline-Cache wird geleert, damit der Airline-Bezug neu greift
    goDownOneRow() {
      if (!this.isLoading) {
        this.resultsAirlines = [];
        this.$emit('rowSelectionChanged', 'down');
      }
    },
    addKeyEventListener() {
      window.addEventListener('keydown', this.handleKeyPress);
    },
    removeKeyEventListener() {
      window.removeEventListener('keydown', this.handleKeyPress);
    },
    // Ermöglicht das Blättern zwischen Reiseterminen per Pfeiltasten links/rechts
    handleKeyPress($event) {
      if ($event.key === 'ArrowLeft') {
        this.goUpOneRow();
      } else if ($event.key === 'ArrowRight') {
        this.goDownOneRow();
      }
    },
    // Baut die Vorbelegung (Verlinkung auf den aktuellen Reisetermin) für das Aufgabe-erstellen-Modal und meldet sie nach außen
    openModalWithData() {
      this.prefixedVerlinkungen = [
        {
          id: 0,
          typ: 'Reisetermin',
          artifactId: this.reisetermin.id,
          artifactText: this.reisetermin.reiseterminkuerzel,
        },
      ];
      this.$emit('showModalAufgabeEdit', this.prefixedVerlinkungen);
    },
    // Lädt die Änderungshistorie aus drei Quellen (Reisetermin, Flugeinkauf, Metadata) parallel und führt sie zusammen
    async loadVerlauf(reiseterminId, reiseId, startdatum) {
      if (reiseterminId) {
        try {
          const [reiseterminHistoryResponse, flugeinkaufHistoryResponse, inlandsfluegeHistoryResponse] =
            await Promise.all([
              apiService.get(`/history/Reisetermine/${reiseterminId}`),
              apiService.get(`/history/Flugeinkauf/${reiseterminId}`),
              apiService.get(`/history/ReiseterminMetadata/${reiseId}/${startdatum}`),
            ]);

          const reiseterminHistory =
            reiseterminHistoryResponse.status === 200 ? reiseterminHistoryResponse.data.result : {};
          const flugeinkaufHistory =
            flugeinkaufHistoryResponse.status === 200 ? flugeinkaufHistoryResponse.data.result : {};
          const inlandsfluegeHistory =
            inlandsfluegeHistoryResponse.status === 200 ? inlandsfluegeHistoryResponse.data.result : {};
          const combinedHistory = { ...reiseterminHistory, ...flugeinkaufHistory, ...inlandsfluegeHistory };

          this.verlaufData = combinedHistory;
        } catch (error) {
          console.error('Fehler beim Laden der Verlaufsdaten:', error);
          this.verlaufData = {};
        }
      }
    },
  },
};
</script>

<style scoped>
.navigationIcons {
  color: black;
}

.navigationIcons:hover {
  color: #0d6efd;
  cursor: pointer;
}

.leftLabel {
  font-size: 1.05rem;
  font-weight: 500;
}

:deep(.form-control) {
  height: 32px !important;
}

.list-group {
  height: 42px;
}
</style>
